import type { DetectorResult } from "../core/types.js";
import type { XPost } from "../sources/x.js";

/**
 * Voice consistency detector.
 *
 * Humans make typos that evolve over time, reuse unique phrases, drift in
 * tone, and use emoji inconsistently. Autonomous agents have frozen voice:
 * stable vocabulary, no typos, consistent phrase templates, predictable
 * emoji usage.
 *
 * Returns a HIGHER score for more consistent (agent-like) voice.
 */
export function voiceDetector(posts: XPost[]): DetectorResult {
  const notes: string[] = [];

  if (posts.length < 15) {
    notes.push(`only ${posts.length} posts, low confidence`);
    return {
      detector: "voice",
      score: 50,
      samples: posts.length,
      notes,
      runtime_ms: 0,
    };
  }

  // Lexical diversity: unique tokens / total tokens.
  const allTokens: string[] = [];
  for (const p of posts) {
    const tokens = tokenize(p.text);
    allTokens.push(...tokens);
  }
  const uniqueRatio = new Set(allTokens).size / allTokens.length;
  notes.push(`lexical diversity: ${uniqueRatio.toFixed(3)}`);

  // Typo detection: very rough heuristic (repeated chars, no-vowel words).
  let typoCount = 0;
  for (const p of posts) {
    if (/([a-z])\1{2,}/i.test(p.text)) typoCount++;
    if (/\b[bcdfghjklmnpqrstvwxyz]{4,}\b/i.test(p.text)) typoCount++;
  }
  const typoRate = typoCount / posts.length;
  notes.push(`typo rate: ${(typoRate * 100).toFixed(1)}%`);

  // Phrase repetition: how many 3-word sequences appear more than once?
  const trigrams = new Map<string, number>();
  for (const p of posts) {
    const tokens = tokenize(p.text);
    for (let i = 0; i < tokens.length - 2; i++) {
      const key = `${tokens[i]} ${tokens[i + 1]} ${tokens[i + 2]}`;
      trigrams.set(key, (trigrams.get(key) ?? 0) + 1);
    }
  }
  const repeated = Array.from(trigrams.values()).filter((v) => v > 1).length;
  const repetitionRate = repeated / Math.max(1, trigrams.size);
  notes.push(`phrase repetition: ${(repetitionRate * 100).toFixed(1)}%`);

  // Emoji consistency: ratio of posts containing any emoji.
  const emojiRegex = /[\p{Emoji_Presentation}]/u;
  const withEmoji = posts.filter((p) => emojiRegex.test(p.text)).length;
  const emojiRatio = withEmoji / posts.length;
  const emojiConsistency = Math.abs(emojiRatio - 0.5) * 2; // 0 or 1 both = consistent
  notes.push(`emoji ratio: ${(emojiRatio * 100).toFixed(0)}%`);

  // Aggregate. Higher score = more agent-like (more consistent).
  let score = 0;
  score += (1 - uniqueRatio) * 100 * 0.35; // lower diversity = more agent
  score += (1 - typoRate) * 100 * 0.25; // fewer typos = more agent
  score += repetitionRate * 100 * 0.2;
  score += emojiConsistency * 100 * 0.2;

  return {
    detector: "voice",
    score: Math.round(Math.max(0, Math.min(100, score))),
    samples: posts.length,
    notes,
    runtime_ms: 0,
  };
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 1);
}
